import { add, format, isAfter, sub } from 'date-fns';
import type { AppAgendaDebate, AppDebate } from '@debatdirect/common/types/debate';

import { screenshotConfig } from '../constants';
import type { DebateEvent, DebateLocation } from '../../types/debatdirect-typings';
import type { LiveStats } from '../../types/liveStats';

/**
 * Rounds the seconds component of a Date object to the nearest specified interval.
 */
const roundSecondsInterval = (date: Date, secondsInterval: number): Date => {
  const rounded = Math.floor(date.getSeconds() / secondsInterval) * secondsInterval;
  const newDate = new Date(date);

  newDate.setSeconds(rounded);

  return newDate;
};

const getScreenshotMoment = ({ status, startedAt, endedAt }: AppDebate | AppAgendaDebate) => {
  const { offsetInSeconds, refreshInterval } = screenshotConfig;

  switch (status) {
    case 'planned':
      return null;
    case 'live':
      // An offset is used to prevent 502 errors when the screenshot is requested too soon
      return roundSecondsInterval(sub(new Date(), { seconds: offsetInSeconds }), refreshInterval);
    case 'vod': {
      const screenshotMoment = add(startedAt, { seconds: offsetInSeconds });

      return isAfter(screenshotMoment, endedAt) ? startedAt : screenshotMoment; // Fail-safe
    }
  }
};

export const getDebateScreenshotUri = (debate: AppDebate | AppAgendaDebate, prefix: string | undefined, isFeatured = false) => {
  const { bitrate, bitrateFeatured, fileType } = screenshotConfig;
  const screenshotBitrate = isFeatured ? bitrateFeatured : bitrate;
  const screenshotMoment = getScreenshotMoment(debate);

  if (!screenshotMoment || !prefix) return null;

  const screenshotDate = format(screenshotMoment, 'yyyy-MM-dd'); // I.E.: 2021-12-31
  const screenshotTime = format(screenshotMoment, 'HH:mm:ssxx'); // I.E.: 23:56:50+0100

  return `${prefix}/${screenshotBitrate}/${screenshotDate}/${screenshotTime}.${fileType}`;
};

export const getDebateMetadata = (debate: AppDebate | AppAgendaDebate) => {
  const date = format(debate.debateDate, 'd MMM');
  const startTime = format(debate.startedAt || debate.startsAt, 'HH:mm');
  const endTime = format(debate.endedAt || debate.endsAt, 'HH:mm');
  const runningTime = `${startTime} - ${endTime}`;
  let description = '';
  if (!debate.locationName) {
    description = debate.debateType;
  } else if (debate.categoryNames.length === 0) {
    description = `${debate.debateType} in de ${debate.locationName}`;
  } else {
    description = `${debate.categoryNames.join(' - ')} in de ${debate.locationName}`;
  }
  return [date, runningTime, description];
};

export const startDateCompareFn = (debateA: AppAgendaDebate, debateB: AppAgendaDebate) => {
  return (debateA.startedAt ?? debateA.startsAt).getTime() - (debateB.startedAt ?? debateB.startsAt).getTime();
};

// Selects a consistent image based on the debate ID: the same debate will always return the same image
// To make the result feel random but stable, we convert the first 4 hex digits of the ID to a number
// That number is then mapped to one of the available images using the modulo operator
export const getDeterministicLocationImage = (debateId: string, locationImages: string[]) => {
  const slice = debateId.slice(0, 4);
  const index = parseInt(slice, 16) % locationImages.length;

  return locationImages[index];
};

export const getDebateFallbackImage = (debateId: string, location: DebateLocation | undefined, isFeatured = false) => {
  if (!location) return undefined;

  const featuredImages = location.imageUrls ?? location.cardImageUrls ?? [location.imageUrl];
  const cardImages = location.cardImageUrls ?? location.imageUrls ?? [location.cardImageUrl];

  return getDeterministicLocationImage(debateId, isFeatured ? featuredImages : cardImages);
};

export const findFirstChairmanEvent = (events: DebateEvent[]) => {
  return events
    .slice()
    .reverse()
    .find(({ eventType }) => eventType === 'chairman' || eventType === 'chairman_selection');
};

export const determineDebateLiveStats = (debate: AppDebate | undefined, pastEvents: DebateEvent[], upcomingEvents: DebateEvent[]): LiveStats => {
  if (!debate?.current) {
    return {
      chairman: null,
      speaker: null,
      interrupter: null,
      speaking: null,
      ended: false,
      suspended: false,
    };
  }

  let breakFor = false;
  let chairman = null;
  let speaker = null;
  let interrupter = null;
  let speaking: LiveStats['speaking'] = null;

  const suspended = pastEvents[0]?.eventType === 'suspended';
  const ended = pastEvents[0]?.eventType === 'debate_end' || pastEvents[0]?.eventType === 'debate_part_end';

  for (let index = 0; index < pastEvents.length; index++) {
    const datum = pastEvents[index];

    switch (datum.eventType) {
      case 'speaker':
      case 'speaker_motion_present':
      case 'speaker_motion_change':
        if (!speaker) {
          speaker = datum.objectId;
        }

        if (!speaking) {
          speaking = 'speaker';
        }
        break;
      case 'chairman_selection':
      case 'chairman':
        if (!chairman) {
          chairman = datum.objectId;
        }
        if (!speaking) {
          speaking = 'chairman';
        }
        break;
      case 'interrupter':
        if (!speaker) {
          if (!interrupter) {
            interrupter = datum.objectId;
          }
          if (!speaking) {
            speaking = 'interrupter';
          }
        }
        break;
      case 'debate_start':
        breakFor = true;
        break;
      default:
    }

    if (breakFor) {
      break;
    }
  }

  if (ended || suspended) {
    speaker = null;
    speaking = null;
  }

  // find the first chairman event in the upcoming data (optimistic) or fallback to the debate current data
  chairman ||= findFirstChairmanEvent(upcomingEvents)?.objectId || debate.current.chairmanId || null;

  return {
    chairman,
    speaker,
    interrupter,
    speaking,
    ended,
    suspended,
  };
};
